import React, { useState, useEffect } from 'react';
import { Card, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import MaterialTable, { MTableToolbar } from 'material-table';
import icons from 'MaterialTableIcons';
import { DetailsColumn } from 'Secure/Utils';
import { EngineIcon } from 'Utils';
import { getSourceNomenclature } from 'Utils/nomenclature';
import { getProviders } from 'Secure/api';
import ProviderSelectCell from 'Secure/ProviderSelectCell';
import EncryptButton from './EncryptButton';
import { getDisplayDate, getDisplayTime } from 'Utils';
import { FaShieldAlt } from 'react-icons/fa';
import { BsFillCircleFill } from 'react-icons/bs';
import { STATUS } from 'Utils/styleTokens';
import DiscoveryTooltip from 'DiscoveryTooltip';

// status.includes('TERMINATING') || status.includes('STARTING') || status.includes('BOOTSTRAPPING')

const statusLabels = {
  ENCRYPTED: 'Encrypted',
  ENCRYPTING: 'Encrypting',
  DECRYPTED: 'Decrypted',
  DECRYPTING: 'Decrypting',
  UNSECURED: 'Unsecured',
  TERMINATING: 'Unsecured',
  STARTING: 'Unsecured',
  BOOTSTRAPPING: 'Unsecured'
};

// Semantic status dots (style-guide STATUS triads): success green, danger red, neutral grey for
// the "not yet secured" states, info blue while running.
const statusColors = {
  ENCRYPTED: STATUS.success.dot,
  ENCRYPTING: STATUS.info.dot,
  DECRYPTED: STATUS.neutral.dot,
  DECRYPTING: STATUS.info.dot,
  UNSECURED: STATUS.neutral.dot,
  ERROR: STATUS.danger.dot,
  TERMINATING: STATUS.neutral.dot,
  STARTING: STATUS.neutral.dot,
  BOOTSTRAPPING: STATUS.neutral.dot,
};

function SourcesTable({ data, fetchingData, fetchDataHandler }) {
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState(null);
  const [searchTerms, setSearchTerms] = useState('');
  const [searched, setSearched] = useState(false);
  const [displayedSearch, setDisplayedSearch] = useState(null);
  const { sources = [], totalConnections = 0 } = data || {};
  const [encryptProviders, setEncryptProviders] = useState([]);
  const PAGE_SIZE = 10;

  useEffect(() => {
    let cancelled = false;
    getProviders('ENCRYPT').then((list) => { if (!cancelled) setEncryptProviders(list); });
    return () => { cancelled = true; };
  }, []);

  const COLUMNS = [
    {
      title: 'DATA SOURCE',
      field: 'connection_name',
      // customSort: () => 0,
      // No explicit width: with tableLayout 'fixed', this column absorbs the leftover
      // horizontal space the other (fixed-width) columns don't use.
      cellStyle: { paddingRight: 0 },
      // Name links to the LAST encrypt/decrypt job's results when one exists (decrypt jobs route
      // to the decrypt results page); before any job has run it falls back to the source editor.
      render: ({ connection_name, dataSourceId, engine, latestJobId, latestJobIsDecrypt }) => (
        <div className='d-flex align-items-center'>
          {engine ? <span className='mr-2'><EngineIcon engine={engine} /></span> : null}
          {latestJobId != null ? (
            <DiscoveryTooltip
              text={`View results of the last ${latestJobIsDecrypt ? 'decryption' : 'encryption'} job`}
              placement='top'
            >
              <Link
                to={`/secure/${latestJobIsDecrypt ? 'decrypt' : 'encrypt'}/results/${encodeURIComponent(latestJobId)}`}
              >
                {connection_name}
              </Link>
            </DiscoveryTooltip>
          ) : dataSourceId != null ? (
            <Link to={`/sources/source-editor/${encodeURIComponent(dataSourceId)}`}>{connection_name}</Link>
          ) : (
            connection_name
          )}
        </div>
      )
    },
    // {
    //   title: 'DISCOVERY DATE',
    //   field: 'time_stamp',
    //   defaultSort: 'desc',
    //   // customSort: () => 0,
    //   width: 200,
    //   render: ({ time_stamp }) => (
    //     <div>{getDisplayDate(time_stamp)} @ {getDisplayTime(time_stamp)}</div>
    //   )
    // },
    {
      title: 'DETAILS',
      field: 'fieldQty',
      width: 200,
      render: ({ dataSourceId, fieldQty, prevSearchid }) => (
        <DetailsColumn
          dataSourceId={dataSourceId}
          fieldQty={fieldQty}
          mode='encrypt'
          prevSearchid={prevSearchid}
        />
      )
    },
    {
      title: 'PROVIDER',
      field: 'preferredEncryptProviderId',
      sorting: false,
      render: (row) => (
        <ProviderSelectCell row={row} providers={encryptProviders}
          field='preferredEncryptProviderId' defaultLabel='C2 Encryption' />
      ),
      width: 180,
      // sorting: false
    },
    {
      title: 'STATUS',
      field: 'status',
      headerStyle: { textAlign: 'center' },
      // Default row order is by most-recent job date (see getEncryptionSources); this column keeps
      // material-table's own sort when the user clicks the header.
      render: ({ status, readOnly, encryptJobProgress, datasourceType }) => {
        const nouns = getSourceNomenclature(undefined, datasourceType);
        if (readOnly) {
          return (
            <div>
              <div className='d-flex justify-content-center'>
                <div className='d-flex flex-column justify-content-center pl-2 pr-2'>
                  <div>
                    <BsFillCircleFill color={STATUS.neutral.dot} />
                  </div>
                </div>
                <div>READ ONLY</div>
              </div>
            </div>
          );
        }
        // Live progress bar while a job runs — mirrors the masking sources table. Backend emits the
        // same workItems/records progress for encrypt/decrypt jobs (jobProgressFromJob).
        if (status === 'ENCRYPTING' || status === 'DECRYPTING') {
          const label = status === 'DECRYPTING' ? 'Decrypting' : 'Encrypting';
          const p = encryptJobProgress;
          const hasTables = p && p.tablesTotal != null && p.tablesTotal > 0 && p.tablesCompleted != null;
          const tip = p && p.currentTableName
            ? `${label} ${p.currentTableName}` + (hasTables ? ` — ${nouns.itemSingular} ${Math.min(p.tablesCompleted, p.tablesTotal)} of ${p.tablesTotal}` : '')
            : label;
          return (
            <DiscoveryTooltip text={tip} placement='top'>
              <div style={{ minWidth: 160 }}>
                <div className='small text-muted mb-1'>{label}</div>
                {/* Encrypt concept is always blue (OPERATION.encrypt) — primary maps to brand blue. */}
                {hasTables ? (
                  <ProgressBar
                    now={Math.min(p.tablesCompleted, p.tablesTotal)}
                    max={p.tablesTotal}
                    label={`${Math.min(p.tablesCompleted, p.tablesTotal)}/${p.tablesTotal} ${nouns.item}`}
                    variant='primary'
                    style={{ height: 22 }}
                  />
                ) : (
                  <ProgressBar animated striped variant='primary' now={100} style={{ height: 22 }} />
                )}
              </div>
            </DiscoveryTooltip>
          );
        }
        return (
          <div>
              <div className='d-flex justify-content-center'>
                { statusColors[status] && (
                  <div className='d-flex flex-column justify-content-center pl-2 pr-2'>
                    <div>
                      <BsFillCircleFill color={statusColors[status]} />
                    </div>
                  </div>
                )}
                <div>
                  { statusLabels[status] ? statusLabels[status] : status }
                </div>
              </div>
          </div>
        );
      }
    },
    {
      title: 'ENCRYPT / DECRYPT',
      headerStyle: { textAlign: 'center' },
      sorting: false,
      render: ({ connection_name, status, errorMessage, fieldQty, dataSourceId, prevSearchid, hasMaskingDriver, readOnly, datasourceType }) => {
        return (
          <EncryptButton
            connection={connection_name}
            datasourceType={datasourceType}
            dataSourceId={dataSourceId}
            status={status}
            errorMessage={errorMessage}
            fieldQty={fieldQty}
            prevSearchid={prevSearchid}
            hasMaskingDriver={hasMaskingDriver}
            readOnly={readOnly}
          />
        );
      },
      width: 250
    }
  ];

  const handleSearch = async () => {
    let searchTermsValue = searchTerms;
    let searchedValue = true;
    let displayedSearchValue = searchTerms;

    if (searched) {
      setSearchTerms('');
      searchTermsValue = null;
      searchedValue = false;
      displayedSearchValue = null;
    }

    await fetchDataHandler(0, null, searchTermsValue);
    setPage(0);
    setSearched(searchedValue);
    setDisplayedSearch(displayedSearchValue);
  }

  return (
    <Card>
      {/* <div className="widget-header d-flex p-3">
        <div className='w-50 text-left d-flex align-items-center'>
          <FaShieldAlt className="mr-2" />
          { displayedSearch ? `Displaying results for "${displayedSearch}"` : 'Encrypt / Decrypt Status'}
        </div>
        <div className='w-50 flex-grow-1 d-flex'>
          <Form.Control
            type='text'
            value={searchTerms}
            placeholder='Search discovered data sources'
            onChange={(e) => {
              setSearchTerms(e.target.value);
              setSearched(false);
            }}
          />
          <Button 
            variant={'outline-secondary'}
            disabled={fetchingData}
            className='pl-4 pr-4 h-100 ml-2'
            onClick={async () => await handleSearch()}
          >
            {fetchingData ? (
              <Spinner size='sm' animation='border' />
            ) : (
              <span>{searched ? 'X' : <FaSearch />}</span>
            )}
          </Button>
        </div>
      </div> */}
      <MaterialTable
        components={{
          Toolbar: props => (
            <div className="entity-panel-table-header d-flex">
              <div className="text-left widget-header p-3">
                <FaShieldAlt className="mr-2" />
                <span>Manage Encryption</span>
              </div>
              <div className='d-flex flex-grow-1 justify-content-end'>
                <MTableToolbar { ...props } />
              </div>
            </div>
          )
        }}
        columns={COLUMNS}
        icons={icons}
        data={sources}
        isLoading={fetchingData}
        options={{
          showTitle: false,
          pageSize: PAGE_SIZE,
          // search: true,
          tableLayout: 'fixed',
          draggable: false
        }}
        // onOrderChange={async (colIndex, dir) => {
        //   if (COLUMNS[colIndex]) {
        //     const col = COLUMNS[colIndex];
        //     const sortField = col.field === 'connection_name' ? '_key' : col.field;
        //     const sortKeyValue = { [sortField]: dir };
        //     await fetchDataHandler(0, sortKeyValue);
        //     setSortKey(sortKeyValue);
        //     setPage(0);
        //   }
        // }}
        // components={{
        //   Pagination: props => (
        //     <MTablePagination
        //       {...props}
        //       classes={{root: props.classes.root}}
        //       count={totalConnections}
        //       showFirstLastPageButtons={false}
        //       page={page}
        //       onChangePage={async (e, pageValue) => {
        //         const fromValue = pageValue * PAGE_SIZE;
        //         await fetchDataHandler(fromValue, sortKey, (searched ? searchTerms : null));
        //         setPage(pageValue);
        //       }}
        //     />
        //   )
        // }}
      />
    </Card>
  )
}

export default SourcesTable;